import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth } from '../../Services/firebase-config';
import { db, doc, getDoc, updateDoc } from '../../Services/firebase-config';

// Helper function to format numbers with commas
const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === '') return '0';
  const number = parseFloat(value);
  if (isNaN(number)) return value.toString();
  return number.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const EquipmentCosts = () => {
  const [depreciationTableData, setDepreciationTableData] = useState([]);
  const [showDepreciationModal, setShowDepreciationModal] = useState(false);
  const [kibaloName, setKibaloName] = useState('');
  const [basisOfCosting, setBasisOfCosting] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [depreciationFormData, setDepreciationFormData] = useState({
    costCenter: '', unit: '', quantity: '', unitCost: '', lifespan: '',
  });
  const [editDepreciationIndex, setEditDepreciationIndex] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const navigate = useNavigate();
  const { kibaloId } = useParams();
  const cleanKibaloId = kibaloId ? kibaloId.split('/')[0].replace(':', '') : null;
  const user = auth.currentUser;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (user && cleanKibaloId) {
        const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);
        const kibaloSnap = await getDoc(kibaloRef);
        if (kibaloSnap.exists()) {
          const data = kibaloSnap.data();
          setKibaloName(data.name || '');
          setDepreciationTableData(data.depreciationTable || []);

          // Set basis of costing text if available
          const costingBasis = data.costingBasis;
          if (costingBasis) {
            const basisText = costingBasis.costBasis === 'year'
              ? `Production per year: ${formatNumberWithCommas(costingBasis.quantity)} ${costingBasis.unit}`
              : `Production per acreage: (${formatNumberWithCommas(costingBasis.quantity)} acres, ${formatNumberWithCommas(costingBasis.productionPerAcre)} ${costingBasis.unit}/year)`;
            setBasisOfCosting(basisText);
          }
        }
      }
      setLoading(false);
    };
    fetchData();
  }, [user, cleanKibaloId]);

  const calculateDepreciationTotal = () =>
    depreciationTableData.reduce((sum, row) => sum + (parseFloat(row.depreciationAmount) || 0), 0).toFixed(2);

  const handleDepreciationSubmit = async () => {
    const { costCenter, unit, quantity, unitCost, lifespan } = depreciationFormData;
    if (costCenter && unit && quantity && unitCost && lifespan) {
      setSaving(true);
      const depreciationAmount = ((Number(quantity) * Number(unitCost)) / Number(lifespan)).toFixed(2);
      const newRow = { costCenter, unit, quantity, unitCost, lifespan, depreciationAmount };

      const updatedData = editDepreciationIndex !== null
        ? depreciationTableData.map((row, i) => i === editDepreciationIndex ? newRow : row)
        : [...depreciationTableData, newRow];

      const kibaloRef = doc(db, 'users', user.uid, 'kibalos', cleanKibaloId);

      try {
        await updateDoc(kibaloRef, { depreciationTable: updatedData });
        setDepreciationTableData(updatedData);
        setDepreciationFormData({ costCenter: '', unit: '', quantity: '', unitCost: '', lifespan: '' });
        setEditDepreciationIndex(null);
        setShowDepreciationModal(false);
      } catch (error) {
        console.error('Error saving depreciation data:', error);
        alert('Failed to save data. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleEditDepreciation = (index) => {
    setDepreciationFormData(depreciationTableData[index]);
    setEditDepreciationIndex(index);
    setShowDepreciationModal(true);
  };

  const handleCloseModal = () => {
    setShowDepreciationModal(false);
    setDepreciationFormData({ costCenter: '', unit: '', quantity: '', unitCost: '', lifespan: '' });
    setEditDepreciationIndex(null);
  };

  const handleDeleteRow = async (index) => {
    setItemToDelete(index);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete === null) return;
    const updatedData = depreciationTableData.filter((_, i) => i !== itemToDelete);
    setDepreciationTableData(updatedData);
    const kibaloRef = doc(db, 'users', user.uid, 'kibalos', cleanKibaloId);
    await updateDoc(kibaloRef, { depreciationTable: updatedData });
    setShowDeleteModal(false);
    setItemToDelete(null);
  };


  if (loading) {
    return (
      <div className="p-6 bg-gray-100 min-h-screen">
        <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-xl p-8 space-y-6 border border-gray-200">
          <div className="h-6 bg-gray-300 rounded w-3/4 mb-4 animate-pulse"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-300 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-300 rounded w-5/6 animate-pulse"></div>
            <div className="h-4 bg-gray-300 rounded w-4/6 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-xl p-8 space-y-6 border border-gray-200">

        {/* HEADER */}
        <h1 className="text-2xl font-bold text-gray-800 tracking-wide">
          DEPRECIATION COSTS FOR {kibaloName} ENTERPRISE (ALL ASSETS)
        </h1>

        {/* BASIS OF COSTING */}
        {basisOfCosting && (
          <p className="text-md font-semibold text-gray-600">{basisOfCosting}</p>
        )}

        {/* TABLE */}
        <div className="overflow-x-auto rounded-lg border border-gray-300 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-700 uppercase text-xs font-semibold">
              {['Cost Center', 'Unit', 'Quantity', 'Unit Cost', 'Lifespan', 'Depreciation Amount', 'Actions'].map(title => (
                <th key={title} className="border px-4 py-3 text-left">{title}</th>
              ))}
            </thead>

            <tbody className="divide-y divide-gray-200">
              {depreciationTableData.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-6 text-gray-500">
                    No equipment costs added yet.
                  </td>
                </tr>
              )}

              {depreciationTableData.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50 transition">
                  <td className="border px-4 py-3">{row.costCenter}</td>
                  <td className="border px-4 py-3">{row.unit}</td>
                  <td className="border px-4 py-3">{formatNumberWithCommas(row.quantity)}</td>
                  <td className="border px-4 py-3">{formatNumberWithCommas(row.unitCost)}</td>
                  <td className="border px-4 py-3">{formatNumberWithCommas(row.lifespan)}</td>
                  <td className="border px-4 py-3">{formatNumberWithCommas(row.depreciationAmount)}</td>
                  <td className="border px-4 py-3 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleEditDepreciation(index)}
                        className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md text-xs"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteRow(index)}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              <tr className="bg-gray-100 font-semibold">
                <td colSpan="5" className="border px-4 py-3 text-right">Total</td>
                <td className="border px-4 py-3">{formatNumberWithCommas(calculateDepreciationTotal())}</td>
                <td className="border px-4 py-3"></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Add Cost Button */}
        <div className="pt-4">
          <button
            onClick={() => setShowDepreciationModal(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow text-sm font-medium"
          >
            Add Asset Cost
          </button>
        </div>

        {/* Navigation Buttons */}
        <div className="mt-6 flex justify-between gap-2">
          <button
            onClick={() => navigate(`/establishment-costing/${cleanKibaloId}`)}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg shadow text-sm font-medium"
          >
            Back to Establishment
          </button>

          <button
            onClick={() => navigate(`/maintenance-costing/${cleanKibaloId}`)}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow text-sm font-medium"
          >
            Go to Other Annual Fixed costs
          </button>
        </div>
      </div>

      {/* MODAL */}
      {showDepreciationModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm z-50">
          <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-xl">
            <h2 className="text-xl font-semibold mb-4 border-b pb-2">
              {editDepreciationIndex !== null ? 'Edit Depreciation Cost' : 'Add Depreciation Cost'}
            </h2>

            <div className="space-y-3">
              {['costCenter', 'unit', 'quantity', 'unitCost', 'lifespan'].map(field => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 capitalize">{field}</label>
                  <input
                    type={['quantity', 'unitCost', 'lifespan'].includes(field) ? 'number' : 'text'}
                    value={depreciationFormData[field]}
                    onChange={e => setDepreciationFormData({ ...depreciationFormData, [field]: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    disabled={saving}
                  />
                </div>
              ))}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={handleDepreciationSubmit}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow text-sm flex items-center"
              >
                {saving ? 'Saving...' : editDepreciationIndex !== null ? 'Save Changes' : 'Add Cost'}
              </button>

              <button
                onClick={handleCloseModal}
                disabled={saving}
                className="px-4 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded-lg shadow text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-1/3">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p>Are you sure you want to delete this item?</p>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="ml-2 bg-gray-300 text-gray-700 px-4 py-2 rounded-md shadow hover:bg-gray-400"
              >
                Cancel
              </button>
              <button onClick={confirmDelete} className="bg-red-500 text-white px-4 py-2 rounded-md shadow hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentCosts;
